import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} SortOption
 * @property {string} label - Display text for the option
 * @property {string} value - Value for the option
 */

/**
 * Custom hook for calculating dropdown alignment based on available viewport space
 */
const useDropdownAlignment = () => {
  /** @type {React.RefObject<HTMLButtonElement>} */
  const buttonRef = useRef(null);

  const [dropdownAlignment, setAlignment] = useState(
    /** @type {'left' | 'right'} */ ('left'),
  );

  useEffect(() => {
    const calculateAlignment = () => {
      const btn = buttonRef.current;
      if (!btn || typeof btn.getBoundingClientRect !== 'function') return;

      const btnRect = btn.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const spaceOnRight = viewportWidth - btnRect.right;
      const spaceOnLeft = btnRect.left;

      // Use right alignment if more space on left, otherwise left alignment
      setAlignment(spaceOnLeft > spaceOnRight ? 'right' : 'left');
    };

    calculateAlignment();
    window.addEventListener('resize', calculateAlignment);
    return () => window.removeEventListener('resize', calculateAlignment);
  }, []);

  return { buttonRef, dropdownAlignment };
};

/**
 * SortBy Component
 * A dropdown component for sorting items with multiple options
 *
 * @param {Object} props
 * @param {Array<SortOption>} props.options - Array of sort options
 * @param {string} props.value - Currently selected sort value
 * @param {Function} props.onChange - Callback when sort option is selected
 * @param {string} props.className - Additional CSS classes
 */
const SortBy = ({ options, onChange, value = '', className = '' }) => {
  const { buttonRef, dropdownAlignment } = useDropdownAlignment();

  /**
   * @param {SortOption} selectedOption - The selected option object
   */
  const handleChange = (selectedOption) => {
    onChange(selectedOption.value);
  };

  const selectedOption = options.find((opt) => opt.value === value) || null;

  return (
    <div className={className}>
      <Listbox value={selectedOption} onChange={handleChange}>
        {({ open }) => (
          <div className="relative w-fit">
            <Listbox.Button
              ref={buttonRef}
              className={`flex justify-center items-center px-3 py-1 gap-2 rounded-full border border-deep-blue-70 cursor-pointer ${
                open || selectedOption ? 'bg-blue-05' : ''
              }`}
            >
              <span className="text-sm font-bold text-deep-blue-70">
                {selectedOption ? selectedOption.label : 'Sort by'}
              </span>
              <img
                src="/public/images/chevron-down.svg"
                alt="chevron-down"
                className={`w-4 h-4 transition-transform shrink-0 ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </Listbox.Button>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Listbox.Options
                className={`absolute top-[calc(100%+8px)] text-gray-100 text-sm min-w-full w-max-content max-w-[calc(100vw-2rem)] shadow-soft bg-white rounded-lg overflow-hidden z-10 focus:outline-none ${
                  dropdownAlignment === 'right' ? 'right-0' : 'left-0'
                }`}
              >
                {options.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    className={({ active }) =>
                      `px-4 py-2 cursor-pointer font-extralight hover:bg-soft-blue whitespace-nowrap ${active ? 'bg-soft-blue' : ''}`
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <div className={`${selected ? 'font-semibold' : ''}`}>
                        {option.label}
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
};

SortBy.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default SortBy;
